import { useState, type VFC, useMemo } from 'react';
import { Col, Row, Space, Upload, Tag as AntdTag } from 'antd';
import { Icon } from '@/components/icon';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { notifyError } from 'utils/notifications';
import { RowGutter } from 'common/spacing';
import { useDeleteTemplate, useUploadTemplate } from 'hooks/templates';
import { useUpdateReseller } from 'api/hooks';
import { useUserInfo } from '@/hooks/useUser';
import { Card, Button, Table, PageHeader, Tag } from 'components'; // keep your app's component wrappers
import { GET_TEMPLATE_DATA_LIST, type TemplatesDataList, type TemplatesVariables } from './queries/getTemplateDataList';
import { TemplateEditorDrawer } from './components/TemplateEditorDrawer';
import { TemplateActionButtons } from './components/ActionsButton';

const pickResellerInfo = (templates: TemplatesDataList['templates']['data']) => {
  if (!templates?.length) return {};

  for (const t of templates) {
    const resellerData = t?.reseller;
    if (resellerData?.id) {
      const resellerId = String(resellerData.id);
      const defaultTplId = resellerData?.default_template?.id;

      return { resellerId, defaultTemplateId: defaultTplId };
    }
  }
  return {};
};

export const Page: VFC = () => {
  const { id: userId } = useUserInfo();

  const {
    data: { templates } = {},
    loading: isLoading,
    refetch,
  } = useStrapiQuery<TemplatesDataList, TemplatesVariables>(GET_TEMPLATE_DATA_LIST, {
    variables: { user: userId },
    onError(error) {
      notifyError(error, {
        message: `Error while fetching template list. Please try again.`,
      });
    },
    fetchPolicy: 'cache-and-network',
  });

  const { resellerId, defaultTemplateId } = useMemo(() => pickResellerInfo(templates?.data), [templates?.data]);

  const { mutate: uploadTemplate, isLoading: isUploading } = useUploadTemplate(refetch);
  const { mutate: updateReseller } = useUpdateReseller(refetch);
  const { mutate: deleteTemplate, isLoading: isDeleting } = useDeleteTemplate(refetch);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>(undefined);

  const openEditor = (id: string) => {
    setEditingTemplateId(id);
    setEditorOpen(true);
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setEditingTemplateId(undefined);
  };

  const setAsDefault = (id: string) => {
    if (!resellerId) return;
    // @ts-expect-error no time
    updateReseller({ id: resellerId, values: { default_template: { id } } });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => (
        <Space>
          {text}
          {record.isDefault && (
            // If your app's Tag is different, keep both; otherwise remove AntdTag.
            <>
              <Tag>Default</Tag>
              <AntdTag style={{ display: 'none' }}>Default</AntdTag>
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'Last updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime(),
      render: (value: string | null | undefined) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <TemplateActionButtons
          disableSetDefault={record.id === defaultTemplateId}
          template={record}
          onDelete={tpl => deleteTemplate(tpl.id)}
          onEdit={tpl => openEditor(tpl.id)}
          onSetDefault={tpl => setAsDefault(tpl.id)}
        />
      ),
    },
  ];

  return (
    <Space className="w-100" direction="vertical">
      <PageHeader
        extra={
          <Upload
            accept=".html,.txt,.hbs"
            beforeUpload={() => false}
            disabled={isUploading || isDeleting}
            maxCount={1}
            multiple={false}
            showUploadList={false}
            onChange={info => {
              if (info.fileList?.[0]) uploadTemplate(info.fileList?.[0]);
            }}
          >
            <Button icon={isUploading ? <Icon iconString="spinner" /> : <Icon iconString="upload" />} type="primary">
              Upload template
            </Button>
          </Upload>
        }
        ghost={false}
        title="Templates"
      />

      <Card>
        <Row gutter={RowGutter.Default} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Table
              // @ts-expect-error no time
              columns={columns}
              dataSource={templates?.data ?? []}
              loading={isLoading}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              rowKey="id"
            />
          </Col>
        </Row>

        <TemplateEditorDrawer
          open={editorOpen}
          templateId={editingTemplateId}
          onClose={closeEditor}
          onSaved={refetch}
        />
      </Card>
    </Space>
  );
};
